import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { IconBadge } from '../common/designSystem';
import { groupFeatures, matchesFeatureQuery } from '../../utils/dashboardFeatures';

/**
 * The "Select more" picker: every feature on a plane that isn't one of the
 * three headline cards.
 *
 * The plane can carry thirty-odd features, which is past the point where a
 * dropdown is usable — so this is a searchable dialog. An admin either scrolls
 * the grouped list or types; Enter opens the first match.
 *
 * Features with no company-side page yet are listed but not clickable, marked
 * "No page yet", so the catalogue stays visible without dead-end clicks.
 */
const FeaturePickerDialog = ({ open, onClose, features, onSelect, title, note, groupOrder }) => {
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  // Reset the search each time it opens — a stale query would hide most of
  // the list before the admin has typed anything.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const matches = useMemo(
    () => features.filter((feature) => matchesFeatureQuery(feature, query)),
    [features, query]
  );
  const sections = useMemo(() => groupFeatures(matches, groupOrder), [matches, groupOrder]);
  const firstSelectable = matches.find((feature) => Boolean(feature.manage)) || null;

  const choose = (feature) => {
    onSelect(feature);
    onClose();
  };

  return (
    <Transition.Root appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" initialFocus={searchRef} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-shoorah-darkBgColor text-left shadow-xl transition-all">
                {/* header */}
                <div className="p-6 pb-4 flex items-start justify-between gap-4">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-shoorah-lightBlack dark:text-shoorah-darkBg"
                    >
                      {title}
                    </Dialog.Title>
                    <p className="text-xs text-shoorah-gray3 mt-[2px]">
                      {note ? `${note} · ` : ''}
                      {features.length} feature{features.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="p-1 rounded-full text-shoorah-gray4 dark:text-shoorah-darkBg hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgTabColor focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* search — Enter opens the first result so the keyboard path
                    never needs the mouse */}
                <form
                  className="px-6 pb-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (firstSelectable) choose(firstSelectable);
                  }}
                >
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-shoorah-gray3" />
                    <input
                      ref={searchRef}
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Search features"
                      aria-label="Search features"
                      className="w-full h-11 pl-10 pr-10 rounded-lg border border-shoorah-inputBorder dark:border-shoorah-darkBorder bg-white dark:bg-shoorah-darkBgTabColor text-sm text-shoorah-lightBlack dark:text-shoorah-darkBg placeholder:text-shoorah-gray3 focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active"
                    />
                    {query && (
                      <button
                        type="button"
                        onClick={() => {
                          setQuery('');
                          searchRef.current?.focus();
                        }}
                        aria-label="Clear search"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-shoorah-gray4 dark:text-shoorah-darkBg hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgColor focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </form>

                {/* results */}
                <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto">
                  {sections.length === 0 ? (
                    <div className="py-10 text-center">
                      <p className="text-sm text-shoorah-lightBlack dark:text-shoorah-darkBg">
                        No features match “{query}”
                      </p>
                      <p className="text-xs text-shoorah-gray3 mt-1">
                        Try a shorter search, or clear it to see all {features.length} features.
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-5">
                      {sections.map((section) => (
                        <div key={section.group}>
                          <p className="text-[11px] uppercase tracking-wide text-shoorah-gray3 mb-2">
                            {section.group}
                          </p>
                          <div className="flex flex-col gap-1">
                            {section.features.map((feature) => {
                              const selectable = Boolean(feature.manage);
                              return (
                                <button
                                  key={feature.key}
                                  type="button"
                                  disabled={!selectable}
                                  onClick={() => choose(feature)}
                                  className={`flex items-center gap-3 w-full text-left rounded-input px-3 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active ${
                                    selectable
                                      ? 'hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgTabColor cursor-pointer'
                                      : 'opacity-60 cursor-not-allowed'
                                  }`}
                                >
                                  <IconBadge src={feature.icon} size="sm" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg truncate">
                                      {feature.name}
                                    </p>
                                    {feature.unit && (
                                      <p className="text-xs text-shoorah-gray3 truncate">
                                        {feature.unit}
                                      </p>
                                    )}
                                  </div>
                                  {!selectable && (
                                    <span className="shrink-0 text-[11px] px-2 py-1 rounded-full bg-shoorah-normanGray dark:bg-shoorah-darkBgTabColor text-shoorah-gray4 dark:text-shoorah-darkBg">
                                      No page yet
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

FeaturePickerDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  features: PropTypes.array.isRequired,
  // Heading order for the grouped list. Omitted means the B2B vocabulary;
  // the super-admin dashboard passes its own.
  groupOrder: PropTypes.arrayOf(PropTypes.string),
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
  note: PropTypes.string
};

FeaturePickerDialog.defaultProps = {
  title: 'Select a feature',
  note: null
};

export default FeaturePickerDialog;
